import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DownloadSimple, 
  Database, 
  Clock, 
  CheckCircle,
  Warning,
  Spinner,
  FileZip,
  ArrowClockwise,
  CalendarBlank,
  Timer,
  Gear,
  Play,
  Stop
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupRestore = ({ adminId }) => {
  const [loading, setLoading] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  
  // Scheduled backup states
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState('daily'); // daily or weekly
  const [scheduleTime, setScheduleTime] = useState('02:00');
  const [scheduleDay, setScheduleDay] = useState('monday'); // for weekly
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [lastAutoBackup, setLastAutoBackup] = useState(null);

  // Issue 21 — backup FORM (date-range + content) + password confirm + JSON.
  const [dateRange, setDateRange] = useState('all'); // today|week|month|custom|all
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [incDatabase, setIncDatabase] = useState(true);
  const [incChats, setIncChats] = useState(true);
  const [incPhotos, setIncPhotos] = useState(false);
  const [incReadme, setIncReadme] = useState(true);
  const [backupPassword, setBackupPassword] = useState('');
  const [jsonLoading, setJsonLoading] = useState(false);

  // Issue 21 — RESTORE (upload) state.
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoring, setRestoring] = useState(false);
  // Batch-2 Point 2 — server-side pre-restore snapshots (one-click undo).
  const [serverBackups, setServerBackups] = useState([]);

  const loadServerBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backup/list-json`);
      setServerBackups(res.data?.backups || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { loadServerBackups(); }, []);

  const handleRestoreFromServer = async (filename, label) => {
    if (!restorePassword.trim()) { toast.error('नीचे Restore box में अपना admin password डालें, फिर snapshot चुनें'); return; }
    if (!window.confirm(`⚠️ "${label}" से restore करें? इससे पहले current data का auto-backup भी बन जाएगा।`)) return;
    try {
      setRestoring(true);
      toast.info('Restore हो रहा है...');
      const res = await axios.post(`${API}/admin/backup/restore-from-server`, {
        admin_id: adminId, password: restorePassword, filename,
      });
      if (res.data?.success) {
        const cols = Object.keys(res.data.restored || {}).length;
        toast.success(`Restore पूरा हुआ! (${cols} collections)`);
        loadServerBackups();
      }
    } catch (error) {
      const msg = error?.response?.status === 403 ? 'गलत password' : (error?.response?.data?.detail || 'Restore fail हुआ');
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  const handleDownloadJson = async () => {
    if (!backupPassword.trim()) { toast.error('Confirm with your admin password'); return; }
    try {
      setJsonLoading(true);
      toast.info('JSON backup बन रहा है...');
      const res = await axios.post(`${API}/admin/backup/export-json`, {
        admin_id: adminId,
        password: backupPassword,
        date_range: dateRange,
        from_date: dateRange === 'custom' ? fromDate : null,
        to_date: dateRange === 'custom' ? toDate : null,
        include_database: incDatabase,
        include_chats: incChats,
        include_photos: incPhotos,
        include_readme: incReadme,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.download = `DWS_Backup_${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('JSON backup download हो गया!');
    } catch (error) {
      const msg = error?.response?.status === 403 ? 'गलत password' : 'JSON backup fail हुआ';
      toast.error(msg);
    } finally {
      setJsonLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) { toast.error('पहले backup file चुनें (JSON या ZIP)'); return; }
    if (!restorePassword.trim()) { toast.error('अपना admin password डालें'); return; }
    if (!window.confirm('⚠️ Restore पुराना data REPLACE कर देगा। पक्का आगे बढ़ें?')) return;
    try {
      setRestoring(true);
      toast.info('Restore हो रहा है... कृपया प्रतीक्षा करें');
      const fd = new FormData();
      fd.append('file', restoreFile);
      fd.append('admin_id', adminId);
      fd.append('password', restorePassword);
      const res = await axios.post(`${API}/admin/backup/restore`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        const cols = Object.keys(res.data.restored || {}).length;
        toast.success(`Restore पूरा हुआ! (${cols} collections)`);
        setRestoreFile(null);
        setRestorePassword('');
        loadServerBackups();
      }
    } catch (error) {
      const msg = error?.response?.status === 403 ? 'गलत password या admin नहीं' : (error?.response?.data?.detail || 'Restore fail हुआ');
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  // Load backup info on mount
  useEffect(() => {
    loadBackupInfo();
    loadScheduleSettings();
  }, []);

  const loadBackupInfo = async () => {
    try {
      setLoadingInfo(true);
      const response = await axios.get(`${API}/admin/backup/info`);
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Failed to load backup info:', error);
      // No backup exists yet - that's okay
      setBackupInfo(null);
    } finally {
      setLoadingInfo(false);
    }
  };

  const loadScheduleSettings = async () => {
    try {
      setScheduleLoading(true);
      const response = await axios.get(`${API}/admin/backup/schedule`);
      if (response.data) {
        setScheduleEnabled(response.data.enabled || false);
        setScheduleFrequency(response.data.frequency || 'daily');
        setScheduleTime(response.data.time || '02:00');
        setScheduleDay(response.data.day || 'monday');
        setLastAutoBackup(response.data.last_auto_backup || null);
      }
    } catch (error) {
      console.error('Failed to load schedule settings:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true);
      const response = await axios.post(`${API}/admin/backup/schedule`, {
        enabled: scheduleEnabled,
        frequency: scheduleFrequency,
        time: scheduleTime,
        day: scheduleDay,
        admin_id: adminId
      });
      
      if (response.data.success) {
        toast.success(scheduleEnabled ? 'Automatic backup schedule सेव हो गया!' : 'Automatic backup बंद हो गया');
      }
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast.error('Schedule save करने में error हुआ');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!backupPassword.trim()) { toast.error('Confirm with your admin password'); return; }
    try {
      setLoading(true);
      toast.info('Backup बन रहा है... कृपया प्रतीक्षा करें');

      // Create backup
      const response = await axios.post(`${API}/admin/backup/create`, {
        admin_id: adminId,
        password: backupPassword
      });

      if (response.data.success) {
        toast.success('ZIP Backup सफलतापूर्वक बन गया!');
        
        // Trigger download
        window.location.href = `${API}/admin/backup/download`;
        
        // Reload backup info
        await loadBackupInfo();
      }
    } catch (error) {
      console.error('Backup failed:', error);
      const msg = error.response?.status === 403 ? 'गलत password' : (error.response?.data?.detail || 'Backup बनाने में error हुआ');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = () => {
    if (!backupInfo?.exists) {
      toast.error('कोई backup उपलब्ध नहीं है। पहले backup बनाएं।');
      return;
    }
    window.location.href = `${API}/admin/backup/download`;
    toast.success('Download शुरू हो गया!');
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('hi-IN', { timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <Database size={24} weight="duotone" className="text-indigo-600" />
            Backup & Restore
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            पूरी Application का backup लें - Code + Database + Settings
          </p>
        </div>
      </div>

      <div className="border-t pt-4">
        {/* Backup Info Card */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <FileZip size={40} weight="duotone" className="text-indigo-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-indigo-800 mb-1">Full Application Backup</h3>
              <p className="text-sm text-indigo-700 mb-3">
                इस backup में शामिल है:
              </p>
              <ul className="text-sm text-indigo-600 space-y-1 mb-3">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  Database Data (Users, Chats, Messages, Groups)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  Complete Source Code (Frontend + Backend)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  App Settings & Configuration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  Recreation Instructions (README)
                </li>
              </ul>
              
              {/* Last Backup Info */}
              {loadingInfo ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Spinner size={16} className="animate-spin" />
                  Loading...
                </div>
              ) : backupInfo?.exists ? (
                <div className="bg-white rounded-lg p-3 border border-indigo-200">
                  <div className="flex items-center gap-2 text-sm text-green-700 mb-1">
                    <CheckCircle size={16} weight="fill" />
                    <span className="font-medium">Last Backup Available</span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p className="flex items-center gap-2">
                      <Clock size={14} />
                      {formatDateTime(backupInfo.created_at)}
                    </p>
                    <p>Size: {formatFileSize(backupInfo.size)}</p>
                    <p>Filename: {backupInfo.filename}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                  <div className="flex items-center gap-2 text-sm text-yellow-700">
                    <Warning size={16} weight="fill" />
                    <span>कोई backup नहीं बना है अभी तक</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>नया backup बनाने पर पुराना backup replace हो जाएगा</li>
                <li>Backup में current state save होता है (deleted items नहीं आएंगे)</li>
                <li>Backup file phone में download करके safe रखें</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Issue 21 — Backup FORM: date range + content + password */}
        <div className="bg-white border border-indigo-200 rounded-lg p-4 mb-4" data-testid="backup-form">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <CalendarBlank size={18} className="text-indigo-600" /> Backup Options
          </h3>

          {/* Date range */}
          <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {[
              { k: 'today', l: 'Today' },
              { k: 'week', l: 'This Week' },
              { k: 'month', l: 'This Month' },
              { k: 'custom', l: 'Custom' },
              { k: 'all', l: 'All Data' },
            ].map((opt) => (
              <button
                key={opt.k}
                onClick={() => setDateRange(opt.k)}
                className={`px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${dateRange === opt.k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                data-testid={`backup-range-${opt.k}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {dateRange === 'custom' && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="backup-from-date" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="backup-to-date" />
              </div>
            </div>
          )}
          <p className="text-[11px] text-gray-400 mb-3">Date range applies to chats/messages. Core data (users, groups, settings) is always included fully.</p>

          {/* Content checkboxes */}
          <label className="block text-sm font-medium text-gray-700 mb-1">Include Content</label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { s: incDatabase, set: setIncDatabase, l: 'Database (Users, Groups, Settings)', k: 'database' },
              { s: incChats, set: setIncChats, l: 'Chats & Messages', k: 'chats' },
              { s: incPhotos, set: setIncPhotos, l: 'Photos/Media (URLs in chats)', k: 'photos' },
              { s: incReadme, set: setIncReadme, l: 'README (restore guide)', k: 'readme' },
            ].map((cb) => (
              <label key={cb.k} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={`backup-inc-${cb.k}`}>
                <input type="checkbox" checked={cb.s} onChange={(e) => cb.set(e.target.checked)} className="w-4 h-4 accent-indigo-600" />
                {cb.l}
              </label>
            ))}
          </div>

          {/* Password confirm */}
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm with your admin password</label>
          <input
            type="password"
            value={backupPassword}
            onChange={(e) => setBackupPassword(e.target.value)}
            placeholder="Admin login password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
            data-testid="backup-password"
          />

          <button
            onClick={handleDownloadJson}
            disabled={jsonLoading || !backupPassword.trim()}
            className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="backup-download-json"
          >
            {jsonLoading ? <><Spinner size={18} className="animate-spin" /> बन रहा है...</> : <><DownloadSimple size={18} weight="bold" /> Download JSON Backup</>}
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleCreateBackup}
            disabled={loading || !backupPassword.trim()}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
            data-testid="backup-create-zip"
          >
            {loading ? (
              <>
                <Spinner size={20} className="animate-spin" />
                Backup बन रहा है...
              </>
            ) : (
              <>
                <FileZip size={20} weight="bold" />
                Create & Download ZIP Backup
              </>
            )}
          </button>

          {backupInfo?.exists && (
            <button
              onClick={handleDownloadBackup}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <ArrowClockwise size={20} weight="bold" />
              Download Last Backup
            </button>
          )}
        </div>

        {/* Usage Guide */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Backup का उपयोग कैसे करें:</h4>
          <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
            <li>Backup download करें और safe रखें (Phone/Computer/Drive)</li>
            <li>Emergency में ZIP file को नए Agent को दें</li>
            <li>Agent README.md पढ़कर पूरी app recreate कर देगा</li>
            <li>Same data, same features, same look - सब वापस!</li>
          </ol>
        </div>

        {/* Issue 21 — RESTORE (upload JSON/ZIP) */}
        <div className="mt-6 border-t pt-6" data-testid="restore-section">
          <div className="flex items-center gap-2 mb-3">
            <ArrowClockwise size={22} weight="duotone" className="text-emerald-600" />
            <div>
              <h3 className="font-semibold text-gray-800">Restore from Backup</h3>
              <p className="text-xs text-gray-500">JSON या ZIP backup file upload करके data वापस लाएं</p>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 flex items-start gap-2">
              <Warning size={18} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">Restore पुराना data <b>REPLACE</b> कर देगा। सिर्फ भरोसेमंद backup file ही restore करें।</p>
            </div>
            <label className="block text-sm font-medium text-emerald-800 mb-1">Backup File (.json या .zip)</label>
            <input
              type="file"
              accept=".json,.zip,application/json,application/zip"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              className="w-full text-sm mb-3 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:text-white file:font-semibold"
              data-testid="restore-file-input"
            />
            <label className="block text-sm font-medium text-emerald-800 mb-1">Confirm with your admin password</label>
            <input
              type="password"
              value={restorePassword}
              onChange={(e) => setRestorePassword(e.target.value)}
              placeholder="Admin login password"
              className="w-full px-3 py-2 border border-emerald-300 rounded-lg text-sm mb-3"
              data-testid="restore-password"
            />
            <button
              onClick={handleRestore}
              disabled={restoring || !restoreFile || !restorePassword.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="restore-submit"
            >
              {restoring ? <><Spinner size={18} className="animate-spin" /> Restore हो रहा है...</> : <><ArrowClockwise size={18} weight="bold" /> Restore Data</>}
            </button>

            {/* Batch-2 Point 2 — auto pre-restore snapshots (one-click undo) */}
            <div className="mt-4 border-t border-emerald-200 pt-3" data-testid="pre-restore-list">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-emerald-800">🛟 Safety Snapshots (auto-backup before each restore)</p>
                <button onClick={loadServerBackups} className="text-[11px] text-emerald-600 font-semibold" data-testid="pre-restore-refresh">Refresh</button>
              </div>
              <p className="text-[11px] text-gray-500 mb-2">गलत restore हो जाए तो नीचे password डालकर इनमें से किसी एक snapshot को एक-click restore करके वापस पा सकते हैं।</p>
              {serverBackups.length === 0 ? (
                <p className="text-[11px] text-gray-400 py-2">अभी कोई safety snapshot नहीं है (पहली restore के बाद अपने-आप बनेगा)।</p>
              ) : (
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {serverBackups.map((b) => (
                    <div key={b.filename} className="flex items-center gap-2 bg-white border border-emerald-200 rounded-lg px-3 py-2" data-testid={`pre-restore-item-${b.filename}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-gray-700 truncate">{b.label}</p>
                        <p className="text-[10px] text-gray-400">{formatDateTime(b.created_at)} · {formatFileSize(b.size)}</p>
                      </div>
                      <button
                        onClick={() => handleRestoreFromServer(b.filename, b.label)}
                        disabled={restoring || !restorePassword.trim()}
                        className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white disabled:opacity-40 flex items-center gap-1 flex-shrink-0"
                        data-testid={`pre-restore-restore-${b.filename}`}
                      >
                        <ArrowClockwise size={12} weight="bold" /> Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Automatic Scheduled Backup Section */}
        <div className="mt-6 border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Timer size={24} weight="duotone" className="text-purple-600" />
              <div>
                <h3 className="font-semibold text-gray-800">Automatic Scheduled Backup</h3>
                <p className="text-xs text-gray-500">बिना याद किए, automatic backup होता रहेगा</p>
              </div>
            </div>
            
            {/* Enable/Disable Toggle */}
            <button
              onClick={() => setScheduleEnabled(!scheduleEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                scheduleEnabled ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {scheduleLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Spinner size={16} className="animate-spin" />
              Loading schedule settings...
            </div>
          ) : (
            <div className={`bg-purple-50 border border-purple-200 rounded-lg p-4 ${!scheduleEnabled ? 'opacity-60' : ''}`}>
              {/* Schedule Status */}
              <div className="flex items-center gap-2 mb-4">
                {scheduleEnabled ? (
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <Play size={16} weight="fill" />
                    <span className="font-medium">Automatic Backup: चालू</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <Stop size={16} weight="fill" />
                    <span className="font-medium">Automatic Backup: बंद</span>
                  </div>
                )}
              </div>

              {/* Frequency Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-purple-800 mb-2">
                    <CalendarBlank size={16} className="inline mr-1" />
                    Frequency (कितनी बार)
                  </label>
                  <select
                    value={scheduleFrequency}
                    onChange={(e) => setScheduleFrequency(e.target.value)}
                    disabled={!scheduleEnabled}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="daily">Daily (रोज़)</option>
                    <option value="weekly">Weekly (हफ्ते में एक बार)</option>
                    <option value="monthly">Monthly (महीने में एक बार)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-purple-800 mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Time (कितने बजे)
                  </label>
                  <select
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    disabled={!scheduleEnabled}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="00:00">12:00 AM (रात 12 बजे)</option>
                    <option value="01:00">01:00 AM (रात 1 बजे)</option>
                    <option value="02:00">02:00 AM (रात 2 बजे)</option>
                    <option value="03:00">03:00 AM (रात 3 बजे)</option>
                    <option value="04:00">04:00 AM (सुबह 4 बजे)</option>
                    <option value="05:00">05:00 AM (सुबह 5 बजे)</option>
                    <option value="06:00">06:00 AM (सुबह 6 बजे)</option>
                    <option value="12:00">12:00 PM (दोपहर 12 बजे)</option>
                    <option value="18:00">06:00 PM (शाम 6 बजे)</option>
                    <option value="21:00">09:00 PM (रात 9 बजे)</option>
                    <option value="23:00">11:00 PM (रात 11 बजे)</option>
                  </select>
                </div>
              </div>

              {/* Day Selection for Weekly */}
              {scheduleFrequency === 'weekly' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-purple-800 mb-2">
                    Day (कौन सा दिन)
                  </label>
                  <select
                    value={scheduleDay}
                    onChange={(e) => setScheduleDay(e.target.value)}
                    disabled={!scheduleEnabled}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="sunday">Sunday (रविवार)</option>
                    <option value="monday">Monday (सोमवार)</option>
                    <option value="tuesday">Tuesday (मंगलवार)</option>
                    <option value="wednesday">Wednesday (बुधवार)</option>
                    <option value="thursday">Thursday (गुरुवार)</option>
                    <option value="friday">Friday (शुक्रवार)</option>
                    <option value="saturday">Saturday (शनिवार)</option>
                  </select>
                </div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSaveSchedule}
                disabled={savingSchedule}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {savingSchedule ? (
                  <>
                    <Spinner size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Gear size={18} weight="bold" />
                    Save Schedule Settings
                  </>
                )}
              </button>

              {/* Schedule Summary */}
              {scheduleEnabled && (
                <div className="mt-3 p-2 bg-white rounded border border-purple-200 text-xs text-purple-700">
                  <p className="font-medium">
                    {scheduleFrequency === 'daily'
                      ? `📅 रोज़ ${scheduleTime} बजे automatic backup होगा`
                      : scheduleFrequency === 'monthly'
                      ? `📅 हर महीने की 1 तारीख को ${scheduleTime} बजे backup होगा`
                      : `📅 हर ${scheduleDay === 'sunday' ? 'रविवार' : scheduleDay === 'monday' ? 'सोमवार' : scheduleDay === 'tuesday' ? 'मंगलवार' : scheduleDay === 'wednesday' ? 'बुधवार' : scheduleDay === 'thursday' ? 'गुरुवार' : scheduleDay === 'friday' ? 'शुक्रवार' : 'शनिवार'} को ${scheduleTime} बजे backup होगा`
                    }
                  </p>
                  {lastAutoBackup && (
                    <p className="mt-1 text-gray-500">
                      Last auto backup: {formatDateTime(lastAutoBackup)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Session-48j — Broken image cleanup admin utility */}
          <div className="mt-4 bg-white rounded-lg shadow-sm p-4 border border-rose-200">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-bold text-sm text-rose-800">🧹 Cleanup Broken Media</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Removes messages with empty / missing image URLs that render as blank broken bubbles.
                </div>
              </div>
              <button
                onClick={async () => {
                  if (!window.confirm('Delete all broken image messages? This cannot be undone.')) return;
                  try {
                    const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id;
                    const r = await axios.post(`${BACKEND_URL}/api/admin/messages/cleanup-broken-images`, null, { params: { admin_id: uid } });
                    toast.success(`🧹 Deleted ${r.data?.deleted || 0} broken messages`);
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Cleanup failed'); }
                }}
                className="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold shadow"
                data-testid="cleanup-broken-images-btn"
              >
                🧹 Run Cleanup
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackupRestore;
